// ---------------------------------------------------------------------------
// SourceColumnCombobox — searchable picker for import source columns.
//
// Built to replace native <select> in the import-wizard mapping panel when the
// source file has many columns (county parcel files routinely expose 60+).
//
// Features:
//   - Type-to-filter search (matches any substring of the column name)
//   - "Only show unused" toggle that hides columns already mapped to other
//     target rows (kept available if selected by the current row so the user
//     can still see their current choice)
//   - Visual marker (✓ + gray) when a column is used by another mapping row,
//     but items remain fully selectable — the same source column CAN be
//     legitimately mapped to multiple targets
//   - Keyboard: ↑/↓ to move highlight, Enter to select, Esc to close
//   - Click-outside closes
// ---------------------------------------------------------------------------

import { useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Search, X } from 'lucide-react';

const SCROLLBAR_STYLES = `
.source-column-scrollbar {
  scrollbar-width: auto;
  scrollbar-color: #94a3b8 #f3f4f6;
}
.source-column-scrollbar::-webkit-scrollbar {
  width: 12px;
}
.source-column-scrollbar::-webkit-scrollbar-track {
  background: #f3f4f6;
  border-left: 1px solid #e5e7eb;
}
.source-column-scrollbar::-webkit-scrollbar-thumb {
  background: #94a3b8;
  border-radius: 9999px;
  border: 3px solid #f3f4f6;
}
.source-column-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #64748b;
}
`;

export interface SourceColumnComboboxProps {
  /** Currently selected column (or empty string for unmapped) */
  value: string;
  onChange: (next: string) => void;
  /** All columns present in the source file */
  columns: string[];
  /** Columns already mapped by ANY row (the "used" universe) */
  usedColumns: Set<string>;
  /** Columns the CURRENT row already claims — never marked or hidden */
  ownColumns: Set<string>;
  disabled?: boolean;
  placeholder?: string;
  /** Width class for the trigger; default max-w-[220px] */
  className?: string;
}

export function SourceColumnCombobox({
  value,
  onChange,
  columns,
  usedColumns,
  ownColumns,
  disabled = false,
  placeholder = '— unmapped —',
  className,
}: SourceColumnComboboxProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [onlyUnused, setOnlyUnused] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [popupStyle, setPopupStyle] = useState<CSSProperties>({});

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  const isUsedElsewhere = (col: string): boolean =>
    usedColumns.has(col) && !ownColumns.has(col);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return columns.filter(col => {
      if (onlyUnused && isUsedElsewhere(col)) return false;
      if (!q) return true;
      return col.toLowerCase().includes(q);
    });
  }, [columns, search, onlyUnused, usedColumns, ownColumns]);

  const updatePopupPosition = () => {
    const container = containerRef.current;
    if (!container) return;

    const trigger = container.querySelector('button[type="button"]') as HTMLButtonElement | null;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const viewport = window.visualViewport;
    const viewportWidth = Math.round(viewport?.width ?? window.innerWidth);
    const viewportHeight = Math.round(viewport?.height ?? window.innerHeight);
    const horizontalPadding = 12;
    const gap = 4;
    const desiredWidth = 280;
    const width = Math.min(desiredWidth, Math.max(180, viewportWidth - horizontalPadding * 2));
    const left = Math.min(
      Math.max(horizontalPadding, rect.left),
      Math.max(horizontalPadding, viewportWidth - width - horizontalPadding),
    );

    const spaceBelow = viewportHeight - rect.bottom - horizontalPadding;
    const spaceAbove = rect.top - horizontalPadding;
    const openAbove = spaceBelow < 260 && spaceAbove > spaceBelow;
    const availableHeight = Math.max(0, openAbove ? spaceAbove : spaceBelow);
    const maxHeight = Math.max(120, Math.min(320, availableHeight - gap));
    const top = openAbove
      ? Math.max(horizontalPadding, rect.top - gap - maxHeight)
      : Math.min(rect.bottom + gap, viewportHeight - horizontalPadding - maxHeight);

    setPopupStyle({
      position: 'fixed',
      left,
      top,
      width,
      height: maxHeight,
      maxHeight,
    });
  };

  // Reset highlight when filter changes.
  useEffect(() => {
    setHighlight(0);
  }, [search, onlyUnused]);

  // Focus the search box when opening.
  useEffect(() => {
    if (open) {
      // Tiny delay so the element is mounted before we focus.
      requestAnimationFrame(() => inputRef.current?.focus());
      updatePopupPosition();
    } else {
      setSearch('');
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const handleReposition = () => updatePopupPosition();
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);
    window.visualViewport?.addEventListener('resize', handleReposition);
    window.visualViewport?.addEventListener('scroll', handleReposition);

    return () => {
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
      window.visualViewport?.removeEventListener('resize', handleReposition);
      window.visualViewport?.removeEventListener('scroll', handleReposition);
    };
  }, [open]);

  // Click-outside to close.
  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!containerRef.current?.contains(target) && !popupRef.current?.contains(target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  // Scroll the highlighted row into view.
  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector<HTMLElement>(`[data-idx="${highlight}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [highlight, open]);

  const commit = (col: string) => {
    onChange(col);
    setOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight(h => Math.min(h + 1, filtered.length - 1));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight(h => Math.max(h - 1, 0));
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered[highlight]) commit(filtered[highlight]);
    }
  };

  const displayLabel = value || placeholder;
  const valueIsUsedElsewhere = value ? isUsedElsewhere(value) : false;
  const popupHeight = typeof popupStyle.height === 'number' ? popupStyle.height : 0;
  const listMaxHeight = popupHeight > 0 ? Math.max(120, popupHeight - 78) : 240;

  return (
    <div ref={containerRef} className={`relative ${className ?? 'w-full max-w-[220px]'}`}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(o => !o)}
        className={[
          'w-full flex items-center justify-between gap-1 border rounded-sm px-2 py-1 text-xs focus:outline-hidden focus:ring-1 focus:ring-blue-500',
          disabled
            ? 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
            : value
              ? 'border-gray-300 bg-white text-gray-800 hover:border-gray-400'
              : 'border-gray-200 text-gray-500 bg-gray-50 hover:border-gray-300',
        ].join(' ')}
      >
        <span className="truncate text-left flex-1 min-w-0">
          {valueIsUsedElsewhere && <span className="text-gray-400 mr-1">✓</span>}
          {displayLabel}
        </span>
        <ChevronDown className={`h-3 w-3 shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && createPortal(
        <>
          <style>{SCROLLBAR_STYLES}</style>
          <div
            ref={popupRef}
            role="dialog"
            className="z-9999 flex flex-col bg-white border border-gray-300 rounded-md shadow-lg overflow-hidden"
            style={popupStyle}
            onWheel={e => e.stopPropagation()}
            onKeyDown={handleKeyDown}
          >
            <div className="flex h-full min-h-0 flex-col">
              <div className="p-2 border-b border-gray-200 space-y-1.5 shrink-0">
                <div className="relative">
                  <Search className="h-3 w-3 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2" />
                  <input
                    ref={inputRef}
                    type="text"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder={`Search ${columns.length} columns…`}
                    className="w-full pl-6 pr-6 py-1 text-xs border border-gray-200 rounded-sm focus:outline-hidden focus:ring-1 focus:ring-blue-500"
                  />
                  {search && (
                    <button
                      type="button"
                      onClick={() => setSearch('')}
                      className="absolute right-1 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-700"
                      title="Clear search"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </div>
                <label className="flex items-center gap-1.5 text-[11px] text-gray-600 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={onlyUnused}
                    onChange={e => setOnlyUnused(e.target.checked)}
                    className="h-3 w-3 rounded-sm border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  Only show unused columns
                </label>
              </div>

              <div
                ref={listRef}
                className="source-column-scrollbar flex-1 min-h-0 overflow-y-scroll py-1 scrollbar-gutter-stable"
                style={{ maxHeight: listMaxHeight, overscrollBehavior: 'contain' }}
                onWheel={e => e.stopPropagation()}
              >
                <button
                  type="button"
                  onClick={() => commit('')}
                  className={[
                    'w-full text-left px-2 py-1 text-xs italic text-gray-500',
                    value === '' ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-50',
                  ].join(' ')}
                >
                  — unmapped —
                </button>
                {filtered.length === 0 ? (
                  <div className="px-2 py-3 text-[11px] text-gray-400 text-center">
                    No columns match
                  </div>
                ) : (
                  filtered.map((col, i) => {
                    const used = isUsedElsewhere(col);
                    const isSelected = col === value;
                    const isHighlighted = i === highlight;
                    return (
                      <button
                        type="button"
                        key={col}
                        data-idx={i}
                        onMouseEnter={() => setHighlight(i)}
                        onClick={() => commit(col)}
                        className={[
                          'w-full text-left px-2 py-1 text-xs flex items-center gap-1.5',
                          isHighlighted ? 'bg-blue-50' : '',
                          isSelected ? 'font-semibold text-blue-700' : used ? 'text-gray-400' : 'text-gray-800',
                        ].join(' ')}
                        title={used ? `${col} — already used in another mapping row` : col}
                      >
                        <span className="w-3 shrink-0 text-center">
                          {used ? '✓' : ''}
                        </span>
                        <span className="truncate flex-1">{col}</span>
                      </button>
                    );
                  })
                )}
              </div>

              <div className="px-2 py-1 border-t border-gray-100 bg-gray-50 text-[10px] text-gray-500 flex items-center justify-between shrink-0">
                <span>{filtered.length} shown · {columns.length} total</span>
                <span className="text-gray-400">↑↓ Enter · Esc</span>
              </div>
            </div>
          </div>
        </>,
        document.body,
      )}
    </div>
  );
}
